import { ApiResult } from '@synergy/api';
import { ChipAutocomplete, PoolTextField } from '@synergy/components';
import { Field, FieldProps, Formik, FormikHelpers, FormikProps } from 'formik';
import { FormattedMessage, useIntl } from 'react-intl';
import {
    Headline,
    PoolButton,
    PoolConfirmationModal,
    PoolFormattedMessage,
    PoolFormikTextfield,
    PoolModal,
    PoolModalActions,
    PoolModalContent,
    PoolModalTitle,
    PoolTooltip,
    PoolTypography,
} from '@synergy/components-next';
import { InvoiceNumberSequence } from '@synergy/models';
import { PaloNextThemeProvider } from '@synergy/theme-palo';
import { ReactElement, SyntheticEvent, useEffect, useRef, useState } from 'react';
import { Stack } from '@mui/material';
import { fields, padZeroLeft } from '@synergy/utils';

import * as InvoiceNumberSequenceActions from './store/invoice-number-sequence-actions';
import { InvoiceNumberSequenceFormValues } from './models/invoice-number-sequence-form-values';
import { InvoiceNumberSequenceRequest } from './models/invoice-number-sequence-request';
import { Supplier } from '../../../../common/models/supplier';
import { invoiceNumberSequenceValidationSchema } from './invoice-number-sequence-validation-schema';
import { invoiceNumberSequencesAvailableSuppliersSelector } from './store/invoice-number-sequence-selector';
import { useAppSelector } from '../../../../store/use-appstate-selector';
import { useThunkDispatch } from '../../../../store/use-thunk-dispatch';

export interface InvoiceNumberSequenceModalProps {
    visible: boolean;
    invoiceNumberSequence: InvoiceNumberSequence | undefined;
    onClose: () => void;
}

const fieldNames = fields<InvoiceNumberSequenceFormValues>();

export const InvoiceNumberSequenceModal = (props: InvoiceNumberSequenceModalProps): ReactElement => {
    const { visible, invoiceNumberSequence, onClose } = props;
    const dispatch = useThunkDispatch();
    const { suppliers, formError } = useAppSelector(invoiceNumberSequencesAvailableSuppliersSelector);
    const [warningModal, setWarningModal] = useState(false);
    const formikRef = useRef<FormikProps<InvoiceNumberSequenceFormValues>>(null);
    const intl = useIntl();

    useEffect(() => {
        dispatch(InvoiceNumberSequenceActions.fetchAvailableSuppliers());
    }, [dispatch, visible]);

    useEffect(() => {
        if (!formikRef.current) {
            return;
        }
        formikRef.current.setErrors({ prefix: formError });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formError]);

    const handleSubmit = async (
        values: InvoiceNumberSequenceFormValues,
        formikHelpers: FormikHelpers<InvoiceNumberSequenceFormValues>
    ) => {
        const request: InvoiceNumberSequenceRequest = {
            prefix: values.prefix ?? null,
            firstNumber: values.firstNumber,
            supplierAccountIds: values.suppliers.map(supplier => supplier.id),
        };

        let response: ApiResult<InvoiceNumberSequenceRequest>;
        if (invoiceNumberSequence?.id) {
            response = await dispatch(
                InvoiceNumberSequenceActions.updateInvoiceNumberSequence(
                    invoiceNumberSequence.id,
                    request,
                    invoiceNumberSequence.isDefault
                )
            );
        } else {
            response = await dispatch(InvoiceNumberSequenceActions.createInvoiceNumberSequence(request));
        }

        if (response?.success) {
            handleClose(formikHelpers);
        }
    };

    const initialValues: InvoiceNumberSequenceFormValues = {
        prefix: invoiceNumberSequence?.prefix ?? null,
        firstNumber: invoiceNumberSequence?.nextFreeNumber ?? 1,
        suppliers: invoiceNumberSequence?.suppliers ?? [],
        isDefault: invoiceNumberSequence?.isDefault ?? false,
    };

    const handleSubmitClick = async (formikProps: FormikProps<InvoiceNumberSequenceFormValues>) => {
        if (invoiceNumberSequence?.id) {
            await formikProps.validateForm();

            if (formikProps.isValid) {
                setWarningModal(true);
            }

            return;
        }

        await formikProps.setFieldValue(fieldNames.prefix, formikProps.values.prefix || null);

        formikProps.handleSubmit();
    };

    const handleWarningSubmit = async (formikProps: FormikProps<InvoiceNumberSequenceFormValues>) => {
        setWarningModal(false);

        await formikProps.setFieldValue(fieldNames.prefix, formikProps.values.prefix || null);

        formikProps.handleSubmit();
    };

    const handleSupplierSearch = (_event: SyntheticEvent<Element, Event>, value: string) => {
        let search;
        if (value) {
            search = new URLSearchParams({ search: value });
        }
        dispatch(InvoiceNumberSequenceActions.fetchAvailableSuppliers(search));
    };

    const handleClose = (
        formikProps: FormikProps<InvoiceNumberSequenceFormValues> | FormikHelpers<InvoiceNumberSequenceFormValues>
    ) => {
        formikProps.resetForm();
        onClose();
    };

    return (
        <Formik
            initialValues={initialValues}
            onSubmit={handleSubmit}
            validationSchema={invoiceNumberSequenceValidationSchema(
                intl,
                invoiceNumberSequence?.prefix,
                invoiceNumberSequence?.nextFreeNumber,
                invoiceNumberSequence?.isDefault
            )}
            enableReinitialize={true}
            innerRef={formikRef}
        >
            {(formikProps: FormikProps<InvoiceNumberSequenceFormValues>) => {
                const firstNumberDisabled =
                    !!invoiceNumberSequence?.id && invoiceNumberSequence?.prefix === formikProps.values.prefix;

                let title = 'app.configuration.modal.newInvoiceNumberSequence';
                if (invoiceNumberSequence?.id) {
                    title = invoiceNumberSequence?.isDefault
                        ? 'app.configuration.modal.editStandardInvoiceNumberSequence'
                        : 'app.configuration.modal.editInvoiceNumberSequence';
                }

                return (
                    <PaloNextThemeProvider>
                        <PoolModal open={visible} onClose={() => handleClose(formikProps)}>
                            <PoolModalTitle>
                                <FormattedMessage id={title} />
                            </PoolModalTitle>
                            <PoolModalContent>
                                <Stack gap={4}>
                                    <Stack alignItems="flex-start" gap={1}>
                                        <Stack direction="row" gap={2}>
                                            <PoolFormikTextfield
                                                name="prefix"
                                                label={<FormattedMessage id="app.label.prefix" />}
                                                required={!invoiceNumberSequence?.isDefault}
                                            />
                                            <PoolTooltip
                                                title={
                                                    firstNumberDisabled ? (
                                                        <FormattedMessage id="app.label.prefix.disabled" />
                                                    ) : undefined
                                                }
                                            >
                                                <Field name="firstNumber">
                                                    {({ field, meta }: FieldProps<number>) => (
                                                        <PoolTextField
                                                            {...field}
                                                            label={
                                                                <FormattedMessage
                                                                    id={
                                                                        invoiceNumberSequence?.id
                                                                            ? 'app.label.nextInvoiceNumber'
                                                                            : 'app.label.firstInvoiceNumber'
                                                                    }
                                                                />
                                                            }
                                                            error={meta.touched && !!meta.error}
                                                            isNumeric={true}
                                                            hasThousandSeparator={false}
                                                            disabled={firstNumberDisabled}
                                                            helperText={
                                                                formikProps.errors.firstNumber ===
                                                                    'app.error.invalidInvoiceNumber' &&
                                                                formikProps.touched.firstNumber ? (
                                                                    <FormattedMessage
                                                                        id={formikProps.errors.firstNumber}
                                                                        values={{
                                                                            lastInvoiceNumber:
                                                                                invoiceNumberSequence?.nextFreeNumber
                                                                                    ? invoiceNumberSequence.nextFreeNumber -
                                                                                      1
                                                                                    : 0,
                                                                        }}
                                                                    />
                                                                ) : undefined
                                                            }
                                                            required={true}
                                                        />
                                                    )}
                                                </Field>
                                            </PoolTooltip>
                                        </Stack>

                                        <PoolTypography
                                            typography="caption"
                                            translationId="app.configuration.modal.preview"
                                            values={{
                                                number:
                                                    formikProps.values.prefix && formikProps.values.firstNumber
                                                        ? formikProps.values.prefix +
                                                          padZeroLeft(formikProps.values.firstNumber, 5)
                                                        : '-',
                                            }}
                                            sx={{ ml: 2 }}
                                        />
                                    </Stack>
                                    {!invoiceNumberSequence?.isDefault && (
                                        <Stack gap={2}>
                                            <Headline translationId="app.configuration.modal.suppliers" />
                                            <Field name="suppliers">
                                                {({ field, form, meta }: FieldProps<Supplier[]>) => (
                                                    <ChipAutocomplete
                                                        noOptionsText={<FormattedMessage id="app.common.emptyResult" />}
                                                        options={suppliers}
                                                        getOptionLabel={supplier => `${supplier.id}・${supplier.name}`}
                                                        getChipLabel={supplier => supplier.name}
                                                        onChange={(_e, value) => form.setFieldValue('suppliers', value)}
                                                        value={field.value || []}
                                                        textfieldPlaceholder="app.customer.supplierSearch"
                                                        error={meta.touched && !!meta.error}
                                                        helperText={
                                                            meta.touched && meta.error ? (
                                                                <FormattedMessage id={meta.error} />
                                                            ) : undefined
                                                        }
                                                        onTextFieldValueChange={handleSupplierSearch}
                                                        popperPlacement="top"
                                                    />
                                                )}
                                            </Field>
                                        </Stack>
                                    )}
                                </Stack>
                            </PoolModalContent>
                            <PoolModalActions>
                                <PoolButton variant="outlined" onClick={() => handleClose(formikProps)}>
                                    <FormattedMessage id="app.common.cancel" />
                                </PoolButton>
                                <PoolButton onClick={() => handleSubmitClick(formikProps)}>
                                    <FormattedMessage
                                        id={
                                            invoiceNumberSequence?.isDefault || invoiceNumberSequence?.id
                                                ? 'app.common.save'
                                                : 'app.common.add'
                                        }
                                    />
                                </PoolButton>
                            </PoolModalActions>
                        </PoolModal>

                        <PoolConfirmationModal
                            open={warningModal}
                            title={<FormattedMessage id="app.configuration.invoiceNumberSequence.warning" />}
                            action={<FormattedMessage id="app.common.yesSave" />}
                            onConfirm={() => handleWarningSubmit(formikProps)}
                            onCancel={() => setWarningModal(false)}
                        >
                            <PoolFormattedMessage
                                id="app.configuration.invoiceNumberSequence.warningContent"
                                values={{
                                    number: `${formikProps.values.prefix ?? '-'}${padZeroLeft(
                                        formikProps.values.firstNumber,
                                        5
                                    )}`,
                                }}
                            />
                        </PoolConfirmationModal>
                    </PaloNextThemeProvider>
                );
            }}
        </Formik>
    );
};
